import type { ReactNode } from "react";
import { Meter } from "@/components/dashboard/widgets/Meter";
import { TOUCH_BG } from "@/components/dashboard/widgets/opsColors";
import { ORIGINATION_SOURCE_LABELS } from "@shared/originationConstants";
import type { BdPayload } from "@shared/bdTracker";
import { firstName, plural } from "./format";

function Tile({ label, value, visual, sub }: { label: string; value: number; visual?: ReactNode; sub: string }) {
  return (
    <div className="min-w-0 bg-card border border-border rounded-lg px-3 py-1.5 flex flex-col overflow-hidden">
      <span className="shrink-0 text-[11px] font-medium text-muted-foreground truncate">{label}</span>
      <span className="shrink-0 text-xl font-semibold tabular-nums leading-tight">{value.toLocaleString()}</span>
      {visual}
      <span className="text-[11px] text-muted-foreground truncate">{sub}</span>
    </div>
  );
}

const channel = (t: string) => ((ORIGINATION_SOURCE_LABELS as Record<string, string>)[t] ?? t).toLowerCase();

/** The six BD numbers for the selected person and period. */
export function BdTiles({ data }: { data: BdPayload }) {
  const t = data.tiles;
  const person = data.people.find((p) => p.id === data.who);
  const who = person ? firstName(person.name) : null;
  const channels = t.sourcedByChannel.slice(0, 3).map((c) => `${c.n} ${channel(c.sourceType)}`).join(" · ");
  const mandates = `${t.referralsInYtdMandates} became ${t.referralsInYtdMandates === 1 ? "a mandate" : "mandates"}`;
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2">
      <Tile label={`Touchpoints · ${data.period.label}`} value={t.touchpoints}
        visual={<Meter title="Meetings, calls and emails" segments={[
          { value: t.meetings, className: TOUCH_BG.meeting }, { value: t.calls, className: TOUCH_BG.call }, { value: t.emails, className: TOUCH_BG.email },
        ]} />}
        sub={`${plural(t.meetings, "meeting")} · ${plural(t.calls, "call")} · ${plural(t.emails, "email")}`} />
      <Tile label="Lunches & meetings" value={t.meetings} sub={t.meals ? `${t.meals} over a meal` : "none over a meal yet"} />
      <Tile label="COIs added" value={t.coisAdded}
        sub={who && t.ownedCois !== null ? `${who} owns ${plural(t.ownedCois, "COI")}` : `${plural(t.totalCois, "person", "people")} total`} />
      <Tile label="Prospects sourced" value={t.sourced} sub={channels || "none this period"} />
      <Tile label="Referrals in" value={t.referralsIn} sub={`YTD: ${t.referralsInYtd} · ${mandates}`} />
      <Tile label="Referrals sent" value={t.referralsSent} sub={`YTD: ${t.referralsSentYtd}`} />
    </div>
  );
}
